/* eslint-disable max-classes-per-file */
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { replaceGuidsToMock } from './string.utils';

export const FILTERED_ERRORS = ['very freq useless error'];
export const STOP_WORDS = ['unauthorized'];

type ErrorPayload = {
  url: string;
  method: string;
  params: any;
  status: number;
};

type TAxiosError = AxiosError<{ error?: string }>;

const isHTTPError = (error: Error | AxiosError | ErrorPayload | string): boolean =>
  typeof error === 'object' && 'isAxiosError' in error;

const handleConstructorParam = (param: ErrorPayload | TAxiosError | string): string => {
  if (isHTTPError(param)) {
    const castedParam = param as TAxiosError;
    return `${castedParam.response?.status} ${
      castedParam.response?.data?.error || castedParam.response?.data
    } on ${replaceGuidsToMock(castedParam.response?.config.url!)}`;
  }
  return String(param);
};

const isErrorChild = (obj: any): boolean => {
  if (obj instanceof Error) return true;
  if (obj.prototype) {
    return isErrorChild(obj.prototype);
  }
  return obj instanceof Error;
};

class WError extends Error {
  method?: string;

  params?: object;

  url?: string;

  constructor(payload: ErrorPayload | TAxiosError | string, url?: string, method?: string, params?: object) {
    const msg = handleConstructorParam(payload);
    super(msg);
    this.method = method;
    this.params = params;
    this.url = url;
    this.cause = null;
    this.stack = undefined;
    this.toString = (): string => msg;
  }
}

export class HTTPError extends WError {
  constructor(payload: ErrorPayload | TAxiosError | string, url?: string, method?: string, params?: any) {
    super(payload, url, method, params);
    this.name = 'HTTPError';
  }
}

export class APIError extends WError {
  constructor(payload: ErrorPayload | TAxiosError | string, url?: string, method?: string, params?: any) {
    super(payload, url, method, params);
    this.name = 'APIError';
  }
}

export class InfrastructureError extends WError {
  constructor(msg: ErrorPayload | string) {
    super(msg);
    this.name = 'InfrastructureError';
  }
}

export class QAError extends WError {
  constructor(msg: ErrorPayload | string) {
    super(msg);
    this.name = 'QAError';
  }
}

export class ORYError extends WError {
  constructor(msg: ErrorPayload | string) {
    super(msg);
    this.name = 'OryError';
  }
}

const reportError = (msg: ErrorPayload | string): ErrorPayload | string => {
  Sentry.captureException(new QAError(msg));
  return msg;
};

export const captureError = (err: Error, needToThrow?: boolean): void => {
  let strError = '';
  if (isErrorChild(err)) {
    strError = `${String(err)} ${JSON.stringify(err, Object.getOwnPropertyNames(err))}`;
  } else if (typeof err === 'string') {
    strError = err;
  }
  console.error(strError);
  if (!FILTERED_ERRORS.includes(err.message) && !STOP_WORDS.some((word: string) => strError.includes(word))) {
    Sentry.captureException(err, (scope) => {
      if (isErrorChild(err)) scope.setTag('exceptionType', err.name);
      return scope;
    });
  }
  if (needToThrow) throw err;
};

export const captureMessage = (err: Error | string): void => {
  let strError = '';
  if (isErrorChild(err)) {
    strError = JSON.stringify(err, Object.getOwnPropertyNames(err));
  } else if (typeof err === 'string') {
    strError = err;
  }
  console.warn(strError, 'comment');
  Sentry.captureMessage(strError, 'info');
};

export const makeHumanReadablePopupError = (errStr: string, maxLen: number = 100): string =>
  `... + ${errStr.replace(/(\\|"|{|}|\\n|;)/g, '').slice(-maxLen)}`;

window.reportError = reportError;
